import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  Building2, 
  Mail, 
  Clock, 
  CheckCircle, 
  XCircle, 
  User,
  Search,
  ExternalLink
} from 'lucide-react';
import { useTheme } from '../ui/theme-provider';
import { cn } from '../../lib/utils';
import { collection, query, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { LoadingSpinner } from '../ui/loading-spinner';
import { useToast } from '../../hooks/use-toast';

const PracticeVerificationAdmin = () => {
  const { theme } = useTheme();
  const { toast } = useToast();
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  // Load pending practice verifications
  useEffect(() => {
    loadPendingVerifications();
  }, []);

  const loadPendingVerifications = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const pendingItems = [];
      
      usersSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        if (userData.managedPractices && Array.isArray(userData.managedPractices)) {
          userData.managedPractices.forEach((practice, index) => {
            if (practice.verificationStatus === 'pending') {
              pendingItems.push({
                userId: userDoc.id,
                userEmail: userData.email,
                userName: userData.displayName || userData.name || 'Unknown User',
                practiceIndex: index,
                ...practice,
                userCreatedAt: userData.createdAt,
                userRole: userData.role
              });
            }
          });
        }
      });
      
      // Sort by submission date (newest first)
      pendingItems.sort((a, b) => {
        const dateA = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt);
        const dateB = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt);
        return dateB - dateA;
      });
      
      setPendingVerifications(pendingItems);
    } catch (error) {
      console.error('Error loading pending verifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pending verifications',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (verification, approved) => {
    try {
      setProcessing(verification.id);
      
      // Get the user document
      const userRef = doc(db, 'users', verification.userId);
      const userSnapshot = await getDocs(query(collection(db, 'users')));
      
      let userData = null;
      userSnapshot.forEach((doc) => {
        if (doc.id === verification.userId) {
          userData = doc.data();
        }
      });
      
      if (!userData) {
        throw new Error('User not found');
      }
      
      const updatedPractices = [...userData.managedPractices];
      
      // Update the specific practice - FIXED: Use new Date() instead of serverTimestamp()
      updatedPractices[verification.practiceIndex] = {
        ...updatedPractices[verification.practiceIndex],
        verificationStatus: approved ? 'verified' : 'rejected',
        reviewedAt: new Date(), // CHANGED: Use new Date() instead of serverTimestamp()
        reviewedBy: 'admin',
        rejectionReason: approved ? '' : rejectionReason,
        adminNotes: adminNotes
      };
      
      // Update the user document - FIXED: Use serverTimestamp() only at top level
      await updateDoc(userRef, {
        managedPractices: updatedPractices,
        lastUpdated: serverTimestamp() // This is fine at the top level
      });
      
      toast({
        title: `Practice ${approved ? 'Approved' : 'Rejected'}`,
        description: `${verification.practiceName} has been ${approved ? 'approved' : 'rejected'} for ${verification.userName}`,
      });
      
      // Reload the list
      await loadPendingVerifications();
      
      // Reset form
      setRejectionReason('');
      setAdminNotes('');
      
    } catch (error) {
      console.error('Error processing verification:', error);
      toast({
        title: 'Error',
        description: 'Failed to process verification',
        variant: 'destructive'
      });
    } finally {
      setProcessing(null);
    }
  };

  const filteredVerifications = pendingVerifications.filter(verification => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      verification.practiceName?.toLowerCase().includes(searchLower) ||
      verification.userName?.toLowerCase().includes(searchLower) ||
      verification.userEmail?.toLowerCase().includes(searchLower) ||
      verification.practicePostcode?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="text-muted-foreground mt-4">Loading pending verifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn(
            "text-3xl font-bold tracking-tight",
            theme === 'light' ? 'text-gray-900' : 'text-white'
          )}>
            Practice Verifications
          </h1>
          <p className={cn(
            "text-sm mt-1",
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          )}>
            Review and approve practice management claims
          </p>
        </div>
        <Button onClick={loadPendingVerifications} variant="outline">
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by practice name, user, or postcode..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={cn(
            "pl-10",
            theme === 'light' 
              ? 'bg-white hover:bg-gray-50 focus:bg-white border-gray-300' 
              : 'hover:bg-[#1F1F2E]'
          )}
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              <span className="text-sm font-medium">Pending</span>
              <Badge variant="secondary">{pendingVerifications.length}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Verification List */}
      {filteredVerifications.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className={cn(
              "text-lg font-medium mb-2",
              theme === 'light' ? 'text-gray-900' : 'text-white'
            )}>
              {searchTerm ? 'No matching verifications' : 'No pending verifications'}
            </h3>
            <p className={cn(
              "text-sm",
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            )}>
              {searchTerm ? 'Try adjusting your search terms' : 'All practice verifications are up to date'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredVerifications.map((verification) => (
            <Card key={`${verification.userId}-${verification.practiceIndex}`} className={cn(
              theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
            )}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-purple-600" />
                      <CardTitle className={cn(
                        "text-lg",
                        theme === 'light' ? 'text-gray-900' : 'text-white'
                      )}>
                        {verification.practiceName}
                      </CardTitle>
                      <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                        Pending Review
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        <span>{verification.userName}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="h-4 w-4" />
                        <span>{verification.userEmail}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>
                          Submitted {verification.submittedAt?.toDate ? 
                            verification.submittedAt.toDate().toLocaleDateString() : 
                            new Date(verification.submittedAt).toLocaleDateString()
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Practice Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Practice Address</Label>
                    <div className={cn(
                      "p-2 rounded border text-sm",
                      theme === 'light' ? 'bg-gray-50 border-gray-200' : 'bg-[#1F1F2E] border-gray-700'
                    )}>
                      {verification.practiceAddress}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Postcode</Label>
                    <div className={cn(
                      "p-2 rounded border text-sm",
                      theme === 'light' ? 'bg-gray-50 border-gray-200' : 'bg-[#1F1F2E] border-gray-700'
                    )}>
                      {verification.practicePostcode}
                    </div>
                  </div>
                </div>

                {/* Official Email */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    <Mail className="h-4 w-4 inline mr-1" />
                    Official Practice Email (For Verification)
                  </Label>
                  <div className={cn(
                    "p-2 rounded border text-sm flex items-center justify-between",
                    theme === 'light' ? 'bg-blue-50 border-blue-200' : 'bg-blue-900/20 border-blue-800'
                  )}>
                    <span className="font-mono">{verification.practiceOfficialEmail}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`mailto:${verification.practiceOfficialEmail}`, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use this email to verify the user's association with the practice
                  </p>
                </div>

                {/* Admin Actions */}
                <div className="border-t pt-4 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Admin Notes (Optional)</Label>
                    <Textarea
                      placeholder="Add any verification notes..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className={cn(
                        "min-h-[60px]",
                        theme === 'light' 
                          ? 'bg-white hover:bg-gray-50 focus:bg-white border-gray-300' 
                          : 'hover:bg-[#1F1F2E]'
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Rejection Reason (If Rejecting)</Label>
                    <Textarea
                      placeholder="Explain why this verification is being rejected..."
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className={cn(
                        "min-h-[60px]",
                        theme === 'light' 
                          ? 'bg-white hover:bg-gray-50 focus:bg-white border-gray-300' 
                          : 'hover:bg-[#1F1F2E]'
                      )}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApproval(verification, true)}
                      disabled={processing === verification.id}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {processing === verification.id ? (
                        <LoadingSpinner size="small" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Approve
                    </Button>
                    
                    <Button
                      onClick={() => handleApproval(verification, false)}
                      disabled={processing === verification.id || !rejectionReason.trim()}
                      variant="destructive"
                    >
                      {processing === verification.id ? (
                        <LoadingSpinner size="small" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      Reject
                    </Button>
                  </div>
                  
                  {!rejectionReason.trim() && (
                    <p className="text-xs text-muted-foreground">
                      Rejection reason is required to reject a verification
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PracticeVerificationAdmin;
